import { createAsyncThunk } from "@reduxjs/toolkit";
import { addResearchWorkItemNote } from "api/addResearchWorkItemNote";
import { archiveResearchWorkItem } from "api/archiveResearchWorkItem";
import { assignResearchWorkItem } from "api/assignResearchWorkItem";
import { cancelResearchDemand } from "api/cancelResearchDemand";
import { claimResearchWorkItem } from "api/claimResearchWorkItem";
import { createResearchIntake } from "api/createResearchIntake";
import { getResearchClientProgress } from "api/getResearchClientProgress";
import { getResearchProductivity } from "api/getResearchProductivity";
import { getResearchWorkItem } from "api/getResearchWorkItem";
import { getResearchWorkItemHistory } from "api/getResearchWorkItemHistory";
import { getResearchWorkItems } from "api/getResearchWorkItems";
import { previewResearchIntake } from "api/previewResearchIntake";
import { restoreResearchDemand } from "api/restoreResearchDemand";
import { restoreResearchWorkItem } from "api/restoreResearchWorkItem";
import { transitionResearchWorkItem } from "api/transitionResearchWorkItem";
import { unclaimResearchWorkItem } from "api/unclaimResearchWorkItem";
import { TServiceError } from "types/general";
import { ReduxState } from "store";
import { RESEARCH_SLICE_NAME } from "./constants";
import { getResearchReferenceData } from "./referenceData";
import {
  IResearchAssignmentRequest,
  IResearchClientProgressBucket,
  IResearchClientProgressRequest,
  IResearchHistoryEntry,
  IResearchIntakePreview,
  IResearchIntakeRequest,
  IResearchIntakeResponse,
  IResearchNoteRequest,
  IResearchProductivityDay,
  IResearchProductivityRequest,
  IResearchReferenceData,
  IResearchReasonRequest,
  IResearchSearchResponse,
  IResearchTransitionRequest,
  IResearchUnclaimRequest,
  IResearchWorkItem,
  IResearchWorkItemsRequest,
} from "./types";

type TResearchThunkConfig = {
  rejectValue: TServiceError;
  state: ReduxState;
};

interface IRequestError {
  response?: { status?: number; data?: unknown };
  message?: string;
}

const messageFromData = (data: unknown): string | undefined => {
  if (typeof data === "string" && data.trim()) return data.trim();
  if (typeof data !== "object" || data === null) return undefined;
  const candidate =
    (data as { message?: unknown; error?: unknown }).message ??
    (data as { error?: unknown }).error;
  return typeof candidate === "string" && candidate.trim()
    ? candidate.trim()
    : undefined;
};

export const normalizeResearchError = (error: unknown): TServiceError => {
  const requestError =
    typeof error === "object" && error !== null
      ? (error as IRequestError)
      : ({} as IRequestError);
  const code = requestError.response?.status ?? 0;
  if (code === 409) {
    return {
      code,
      message:
        "This WIP item changed after you opened it. Refresh it and try again.",
    };
  }
  return {
    code,
    message:
      messageFromData(requestError.response?.data) ??
      requestError.message ??
      "The WIP request could not be completed. Please try again.",
  };
};

export const fetchResearchBoard = createAsyncThunk<
  IResearchSearchResponse,
  IResearchWorkItemsRequest,
  TResearchThunkConfig
>(`${RESEARCH_SLICE_NAME}/fetchBoard`, async (request, { rejectWithValue }) => {
  try {
    return await getResearchWorkItems(request);
  } catch (error) {
    return rejectWithValue(normalizeResearchError(error));
  }
});

export const fetchResearchClientProgress = createAsyncThunk<
  IResearchClientProgressBucket[],
  IResearchClientProgressRequest,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/fetchClientProgress`,
  async (request, { rejectWithValue }) => {
    try {
      return await getResearchClientProgress(request);
    } catch (error) {
      return rejectWithValue(normalizeResearchError(error));
    }
  }
);

export const fetchResearchReferenceData = createAsyncThunk<
  IResearchReferenceData,
  void,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/fetchReferenceData`,
  async (_unused, { getState, rejectWithValue }) => {
    try {
      return await getResearchReferenceData(getState().SignInReducer.userInfo);
    } catch (error) {
      return rejectWithValue(normalizeResearchError(error));
    }
  }
);

export const fetchResearchProductivity = createAsyncThunk<
  IResearchProductivityDay[],
  IResearchProductivityRequest,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/fetchProductivity`,
  async (request, { rejectWithValue }) => {
    try {
      return await getResearchProductivity(request);
    } catch (error) {
      return rejectWithValue(normalizeResearchError(error));
    }
  }
);

export const fetchResearchWorkItemDetail = createAsyncThunk<
  IResearchWorkItem,
  string,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/fetchWorkItemDetail`,
  async (workItemId, { rejectWithValue }) => {
    try {
      return await getResearchWorkItem(workItemId);
    } catch (error) {
      return rejectWithValue(normalizeResearchError(error));
    }
  }
);

export const fetchResearchHistory = createAsyncThunk<
  IResearchHistoryEntry[],
  string,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/fetchHistory`,
  async (workItemId, { rejectWithValue }) => {
    try {
      return await getResearchWorkItemHistory(workItemId);
    } catch (error) {
      return rejectWithValue(normalizeResearchError(error));
    }
  }
);

export const previewIntake = createAsyncThunk<
  IResearchIntakePreview,
  IResearchIntakeRequest,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/previewIntake`,
  async (request, { rejectWithValue }) => {
    try {
      return await previewResearchIntake(request);
    } catch (error) {
      return rejectWithValue(normalizeResearchError(error));
    }
  }
);

export const submitIntake = createAsyncThunk<
  IResearchIntakeResponse,
  IResearchIntakeRequest,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/submitIntake`,
  async (request, { rejectWithValue }) => {
    try {
      return await createResearchIntake(request);
    } catch (error) {
      return rejectWithValue(normalizeResearchError(error));
    }
  }
);

type WorkMutationArg<T> = { workItemId: string; request: T };

export const claimWorkItem = createAsyncThunk<
  IResearchWorkItem,
  WorkMutationArg<{ version: number }>,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/claimWorkItem`,
  async ({ workItemId, request }, api) => {
    try {
      return await claimResearchWorkItem(workItemId, request);
    } catch (error) {
      return api.rejectWithValue(normalizeResearchError(error));
    }
  }
);

export const unclaimWorkItem = createAsyncThunk<
  IResearchWorkItem,
  WorkMutationArg<IResearchUnclaimRequest>,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/unclaimWorkItem`,
  async ({ workItemId, request }, api) => {
    try {
      return await unclaimResearchWorkItem(workItemId, request);
    } catch (error) {
      return api.rejectWithValue(normalizeResearchError(error));
    }
  }
);

export const transitionWorkItem = createAsyncThunk<
  IResearchWorkItem,
  WorkMutationArg<IResearchTransitionRequest>,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/transitionWorkItem`,
  async ({ workItemId, request }, api) => {
    try {
      return await transitionResearchWorkItem(workItemId, request);
    } catch (error) {
      return api.rejectWithValue(normalizeResearchError(error));
    }
  }
);

export const addWorkItemNote = createAsyncThunk<
  IResearchWorkItem,
  WorkMutationArg<IResearchNoteRequest>,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/addWorkItemNote`,
  async ({ workItemId, request }, api) => {
    try {
      return await addResearchWorkItemNote(workItemId, request);
    } catch (error) {
      return api.rejectWithValue(normalizeResearchError(error));
    }
  }
);

export const assignWorkItem = createAsyncThunk<
  IResearchWorkItem,
  WorkMutationArg<IResearchAssignmentRequest>,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/assignWorkItem`,
  async ({ workItemId, request }, api) => {
    try {
      return await assignResearchWorkItem(workItemId, request);
    } catch (error) {
      return api.rejectWithValue(normalizeResearchError(error));
    }
  }
);

export const cancelDemand = createAsyncThunk<
  IResearchWorkItem,
  WorkMutationArg<IResearchReasonRequest> & { demandId: string },
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/cancelDemand`,
  async ({ workItemId, demandId, request }, api) => {
    try {
      return await cancelResearchDemand(workItemId, demandId, request);
    } catch (error) {
      return api.rejectWithValue(normalizeResearchError(error));
    }
  }
);

export const restoreDemand = createAsyncThunk<
  IResearchWorkItem,
  { workItemId: string; demandId: string; version: number },
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/restoreDemand`,
  async ({ workItemId, demandId, version }, api) => {
    try {
      return await restoreResearchDemand(workItemId, demandId, version);
    } catch (error) {
      return api.rejectWithValue(normalizeResearchError(error));
    }
  }
);

export const archiveWorkItem = createAsyncThunk<
  IResearchWorkItem,
  WorkMutationArg<IResearchReasonRequest>,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/archiveWorkItem`,
  async ({ workItemId, request }, api) => {
    try {
      return await archiveResearchWorkItem(workItemId, request);
    } catch (error) {
      return api.rejectWithValue(normalizeResearchError(error));
    }
  }
);

export const restoreWorkItem = createAsyncThunk<
  IResearchWorkItem,
  { workItemId: string; version: number },
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/restoreWorkItem`,
  async ({ workItemId, version }, api) => {
    try {
      return await restoreResearchWorkItem(workItemId, version);
    } catch (error) {
      return api.rejectWithValue(normalizeResearchError(error));
    }
  }
);
